/**
 * Card model — per /design/specs/5_game_engine_spec.md §1.1.
 *
 * Canonical card representation: a `rank` and `suit` pair.
 *
 * String form: rank-glyph + suit-letter, e.g. 'As', 'Td', '2c'.
 * 13 ranks × 4 suits = 52 cards in a fresh deck.
 */

export const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const;
export type Rank = (typeof RANKS)[number];

export const SUITS = ['s', 'h', 'd', 'c'] as const;
export type Suit = (typeof SUITS)[number];

export interface Card {
  rank: Rank;
  suit: Suit;
}

/** Numeric rank order for comparisons (2=2, ..., A=14). No ace-low usage in V1. */
export const RANK_VALUE: Record<Rank, number> = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  T: 10, J: 11, Q: 12, K: 13, A: 14,
};

/** Suit ordering for tie-breaking canonicalization: s < h < d < c. */
export const SUIT_VALUE: Record<Suit, number> = { s: 0, h: 1, d: 2, c: 3 };

/** Returns negative if a < b, positive if a > b, zero if equal. */
export function compareCards(a: Card, b: Card): number {
  const rankDiff = RANK_VALUE[a.rank] - RANK_VALUE[b.rank];
  if (rankDiff !== 0) return rankDiff;
  return SUIT_VALUE[a.suit] - SUIT_VALUE[b.suit];
}

export function cardsEqual(a: Card, b: Card): boolean {
  return a.rank === b.rank && a.suit === b.suit;
}

/** Canonical string form: e.g. `{ rank: 'A', suit: 's' }` → 'As'. */
export function cardToString(card: Card): string {
  return `${card.rank}${card.suit}`;
}

/** Parse a 2-character card string. Throws on invalid input. */
export function parseCardString(s: string): Card {
  if (s.length !== 2) {
    throw new Error(`Invalid card string (length must be 2): ${s}`);
  }
  const rank = s[0] as Rank;
  const suit = s[1] as Suit;
  if (!RANKS.includes(rank)) throw new Error(`Invalid rank: ${s[0]}`);
  if (!SUITS.includes(suit)) throw new Error(`Invalid suit: ${s[1]}`);
  return { rank, suit };
}

/** True if `card` is a member of `set`. */
export function containsCard(set: Card[], card: Card): boolean {
  return set.some((c) => cardsEqual(c, card));
}
